import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import api, { fileUrl, fmtMoney, videoEmbed } from "../lib/api";
import { Button } from "../components/ui/button";
import TripGlobe from "../components/TripGlobe";
import TripCountdown from "../components/TripCountdown";
import { flagEmoji } from "../lib/flags";
import GiveDialog from "../components/GiveDialog";
import { openGive } from "../lib/give";
import { CtaButton, LinkedText } from "../components/DispatchFields";
import { Loader2, Compass, MapPin, Heart, Gift, ArrowRight, Globe2, BookOpen, ExternalLink } from "lucide-react";

const cleanName = (n) => (n || "").replace(/[^\p{L}\s,]/gu, "").split(",").pop().trim();

export default function PublicFeed() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound | private
  const [giveOpen, setGiveOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/feed/${encodeURIComponent(slug)}`);
        setData(data);
        setState("ok");
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setState(e?.response?.status === 403 || detail === "not_public" ? "private" : "notfound");
      }
    })();
  }, [slug]);

  const accent = data?.org?.accent_color || "var(--brand-navy)";

  const globeMarkers = useMemo(
    () => (data?.countries || [])
      .filter((c) => typeof c.lat === "number" && typeof c.lng === "number")
      .map((c) => ({ lat: c.lat, lng: c.lng, current: !!c.is_current, name: cleanName(c.name) })),
    [data]
  );
  const origin = useMemo(() => {
    const t = data?.trip;
    return t && t.origin_lat != null && t.origin_lng != null
      ? { lat: t.origin_lat, lng: t.origin_lng, country: cleanName(t.origin_country), city: t.origin_city }
      : null;
  }, [data]);
  const currentCountry = (data?.countries || []).find((c) => c.is_current);

  if (state === "loading")
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: accent }} />
      </div>
    );

  if (state !== "ok")
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5 text-center" data-testid="public-feed-empty">
        <Compass className="h-12 w-12 text-muted-foreground mb-4" />
        <h1 className="font-heading font-bold text-2xl text-[var(--brand-navy)]">
          {state === "private" ? "This journey is private" : "Mission not found"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm">
          {state === "private"
            ? "The team hasn't made their public trip feed available yet. Check back soon."
            : "We couldn't find a public trip at this link. Double-check the URL from the missionary."}
        </p>
        <Link to="/auth" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold accent-text hover:underline">
          Go to sign in
        </Link>
      </div>
    );

  const { org, trip, progress, posts, countries } = data;
  const pct = progress.goal_cents > 0 ? Math.min(100, Math.round((progress.raised_cents / progress.goal_cents) * 100)) : 0;
  const countryFlags = countries.map((c) => ({ name: cleanName(c.name), flag: flagEmoji(c.name) })).filter((f) => f.flag);
  const website = org.website ? (org.website.startsWith("http") ? org.website : `https://${org.website}`) : null;
  const storyUrl = org.story_url ? (org.story_url.startsWith("http") ? org.story_url : `https://${org.story_url}`) : null;
  const tripDiffers = trip.trip_name && trip.trip_name.trim() && trip.trip_name.trim().toLowerCase() !== (org.org_name || "").trim().toLowerCase();
  const headline = tripDiffers ? `Follow ${org.org_name} on ${trip.trip_name}` : `Follow ${org.org_name}`;
  const coverUrl = org.cover_file_id ? fileUrl(`/api/files/${org.cover_file_id}`, 1200) : null;

  return (
    <div className="min-h-screen bg-cream" style={{ "--tenant-accent": accent }} data-testid="public-feed-page">
      {/* Welcome hero */}
      <header className="relative text-white overflow-hidden" style={{ background: coverUrl ? "#1a1a1a" : `linear-gradient(150deg, ${accent}, ${accent}cc)`, paddingTop: "calc(env(safe-area-inset-top) + 40px)" }}>
        {coverUrl ? (
          <>
            <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 22%" }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.20) 45%, rgba(0,0,0,0.35) 75%, ${accent}e0 100%)` }} />
          </>
        ) : (
          <div className="absolute -right-16 -top-16 opacity-10 pointer-events-none"><Globe2 className="h-72 w-72" /></div>
        )}
        <div className="relative mx-auto max-w-2xl px-6 pt-2 pb-10">
          <div className="flex items-center gap-3">
            {org.logo_file_id ? (
              <img src={fileUrl(`/api/files/${org.logo_file_id}`, 200)} alt={org.org_name} className="h-14 w-14 rounded-2xl object-cover shadow-md bg-white/10" />
            ) : (
              <div className="h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center font-heading font-bold text-2xl">{(org.org_name || "M")[0]}</div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-bold text-lg leading-tight truncate" data-testid="public-feed-org-name">{org.org_name}</div>
              {tripDiffers && <div className="text-white/75 text-sm truncate">{trip.trip_name}</div>}
            </div>
          </div>

          <div className="mt-7">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-white/70">Welcome, friend</div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl leading-tight mt-2">
              {headline}
            </h1>
            {org.tagline && <p className="text-white/90 text-[15px] mt-3">{org.tagline}</p>}
            <p className="text-white/80 text-sm mt-3 leading-relaxed">
              Thank you for journeying with us. Follow along below — see where we are, read the latest, and pray every step of the way.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-6">
            {trip.current_location && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
                <MapPin className="h-3.5 w-3.5" /> {trip.current_location}
              </span>
            )}
            {progress.partners > 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
                <Heart className="h-3.5 w-3.5" /> {progress.partners} following
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 -mt-6 pb-16 space-y-5">
        {/* Countdown */}
        {trip.start_date && (
          <div className="rounded-3xl px-6 py-5 text-white shadow-[0_10px_30px_rgba(0,0,0,0.14)]" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)` }} data-testid="public-feed-countdown">
            <TripCountdown startDate={trip.start_date} endDate={trip.end_date} />
          </div>
        )}

        {/* Globe */}
        {(globeMarkers.length > 0 || origin) && (
          <div className="rounded-3xl bg-white px-5 py-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="public-feed-globe">
            <div className="text-xs uppercase tracking-widest accent-text font-semibold text-center">The journey</div>
            <div className="flex justify-center mt-1 -mb-1">
              <TripGlobe markers={globeMarkers} origin={origin} size={280} />
            </div>
            <div className="text-center text-sm text-[#2C2C3E]/80">
              {globeMarkers.length} {globeMarkers.length === 1 ? "country" : "countries"} on the route
              {currentCountry ? <> · currently in <span className="accent-text font-semibold">{cleanName(currentCountry.name)}</span></>
                : origin ? <> · departing from <span className="accent-text font-semibold">{cleanName(origin.country)}</span></> : null}
            </div>
            {countryFlags.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 mt-4 text-2xl" data-testid="public-feed-flags">
                {countryFlags.map((f, i) => <span key={i} title={f.name} className="leading-none">{f.flag}</span>)}
              </div>
            )}
          </div>
        )}

        {/* Mission + progress + give */}
        <div className="bg-white rounded-2xl p-5 shadow-[0_8px_32px_rgba(44,44,62,0.06)] space-y-4">
          {org.mission_statement && (
            <p className="text-[15px] text-[#2C2C3E]/85 leading-relaxed">{org.mission_statement}</p>
          )}
          {progress.goal_cents > 0 && (
            <div data-testid="public-feed-progress">
              <div className="flex items-end justify-between mb-1.5">
                <span className="font-heading font-extrabold text-2xl text-[#2C2C3E]">{fmtMoney(progress.raised_cents, progress.currency)}</span>
                <span className="text-xs text-muted-foreground">of {fmtMoney(progress.goal_cents, progress.currency)} goal</span>
              </div>
              <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: accent }} />
              </div>
              <div className="text-xs text-muted-foreground mt-1">{pct}% funded</div>
            </div>
          )}
          {(slug === "reset" || trip.donate_url) && (
            <Button data-testid="public-feed-give-btn" onClick={() => openGive(trip.donate_url || (slug === "reset" ? "https://akop.live/give" : ""))} className="w-full rounded-full text-white h-12 hover:opacity-90" style={{ background: accent }}>
              <Gift className="h-4 w-4 mr-2" /> {trip.donate_label || "Give to support this trip"}
            </Button>
          )}
        </div>
        <GiveDialog open={giveOpen} onOpenChange={setGiveOpen} donateUrl={trip.donate_url} donateLabel={trip.donate_label} orgSlug={slug} />

        {/* Learn more about us */}
        {(website || storyUrl) && (
          <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)]" data-testid="public-feed-about">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">More about us</div>
            <div className="space-y-2.5">
              {storyUrl && (
                <a href={storyUrl} target="_blank" rel="noreferrer" data-testid="public-feed-story-link"
                  className="flex items-center gap-3 rounded-xl border p-3.5 hover:bg-muted/40 transition-colors">
                  <BookOpen className="h-5 w-5 shrink-0" style={{ color: accent }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2C2C3E]">Read our story</div>
                    <div className="text-xs text-muted-foreground">How this journey began and why we go</div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                </a>
              )}
              {website && (
                <a href={website} target="_blank" rel="noreferrer" data-testid="public-feed-website-link"
                  className="flex items-center gap-3 rounded-xl border p-3.5 hover:bg-muted/40 transition-colors">
                  <Globe2 className="h-5 w-5 shrink-0" style={{ color: accent }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2C2C3E]">Visit our website</div>
                    <div className="text-xs text-muted-foreground truncate">{website.replace(/^https?:\/\//, "")}</div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Feed */}
        <div>
          <h2 className="font-heading font-bold text-lg text-[#2C2C3E] mb-3">Latest updates</h2>
          {posts.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center text-sm text-muted-foreground shadow-[0_4px_24px_rgba(44,44,62,0.04)]">
              No updates posted yet — check back soon.
            </div>
          ) : (
            <div className="space-y-4" data-testid="public-feed-posts">
              {posts.map((p) => <PublicPost key={p.id} p={p} accent={accent} />)}
            </div>
          )}
        </div>

        {/* Follow CTA */}
        <Link to={`/o/${org.slug}`} data-testid="public-feed-follow-cta">
          <div className="rounded-2xl p-5 text-white flex items-center justify-between gap-3 hover:opacity-95 transition-opacity" style={{ background: accent }}>
            <div>
              <div className="font-heading font-bold text-lg leading-tight">Follow the journey</div>
              <div className="text-white/85 text-sm">Get updates, pray along, and see your impact.</div>
            </div>
            <ArrowRight className="h-6 w-6 shrink-0" />
          </div>
        </Link>
      </main>

      <div className="pb-8 text-center text-[11px] text-muted-foreground">Powered by Mission Companion</div>
    </div>
  );
}

function PublicPost({ p, accent }) {
  const when = p.publish_at ? formatDistanceToNow(new Date(p.publish_at), { addSuffix: true }) : "";
  const embed = videoEmbed(p.video_url);
  return (
    <article className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(44,44,62,0.04)]" data-testid="public-post-card">
      {p.media?.length > 0 && (
        <div className="relative">
          <img src={fileUrl(p.media[0], 800)} alt={p.title} className="w-full aspect-[4/5] object-cover" />
          {p.country && (
            <span className="absolute top-3 left-3 text-xs font-medium bg-black/55 text-white px-3 py-1 rounded-full backdrop-blur inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.country}</span>
          )}
        </div>
      )}
      {p.media?.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 pt-3 pb-1">
          {p.media.slice(1).map((m, i) => <img key={m + i} src={fileUrl(m, 200)} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />)}
        </div>
      )}
      {p.videos?.length > 0 && (
        <div className="px-4 pt-3 space-y-2">
          {p.videos.map((v, i) => <video key={v + i} src={fileUrl(v)} controls playsInline className="w-full rounded-xl bg-black aspect-video" />)}
        </div>
      )}
      {embed && embed.type === "iframe" && (
        <div className="aspect-video w-full bg-black"><iframe src={embed.src} title="video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
      )}
      {embed && embed.type === "video" && <video src={embed.src} controls className="w-full aspect-video bg-black" />}
      <div className="p-5">
        <h3 className="font-heading font-bold text-xl text-[#2C2C3E] leading-snug">{p.title}</h3>
        <div className="text-xs text-muted-foreground mt-0.5">{p.author_name} · {when}</div>
        <LinkedText text={p.body} className="text-[15px] text-[#2C2C3E]/90 mt-3 whitespace-pre-wrap leading-relaxed" testid="public-dispatch-body" />
        <CtaButton url={p.link_url} label={p.link_label} accent={accent} testid="public-dispatch-cta-btn" />
        {p.reaction_count > 0 && (
          <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Heart className="h-4 w-4" style={{ color: accent }} /> {p.reaction_count}
          </div>
        )}
      </div>
    </article>
  );
}
